import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { deleteTask, getTasks } from "../../store/user/tasks/tasksActions";
import {
  Box,
  Link,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
} from "@mui/material";
import CustomTable from "../../common/CustomTable/CustomTable";
import {
  CloseOutlined,
  DeleteOutline,
  MoreVertOutlined,
  ArrowBack,
} from "@mui/icons-material";
import moment from "moment";
import { dateFormat } from "../../constants/Constants";
import { RootState } from "../../store/store";
import TasksStyles from "./TasksStyles";
import ReactMarkdown from "react-markdown";
import { ITask } from "../../models/user/Tasks/Tasks";
import ErrorIcon from "@mui/icons-material/Error";
import successIcon from "../../assets/success.svg";
import { setTaskMessage, clearTasks } from "../../store/user/tasks/tasksSlice";
import { resetCommonSlice } from "../../store/common/CommonSlice";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useNavigate } from "react-router-dom";
import { isFeatureAvailable } from "../../utils/Utility";
import { useTranslation } from "react-i18next";
import FilterListIcon from "@mui/icons-material/FilterList";
import Select from "@mui/material/Select";

const Tasks = () => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { tasks, taskMessage } = useAppSelector(
    (state: RootState) => state.tasks
  );
  const { isError, calledReducerType, isSuccess } = useAppSelector(
    (state: RootState) => state.common
  );
  const { features } = useAppSelector((state: RootState) => state.features);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedTask, setSelectedTask] = useState<ITask | null>(null);
  const [deleteTaskDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [selectedStatus, setSelectedStatus] = useState("All Statuses");

  const open = Boolean(anchorEl);

  const handleMoreClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    task: ITask
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteTaskClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteTaskDialogClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedTask(null);
    dispatch(resetCommonSlice());
    dispatch(setTaskMessage(null));
    if (
      userDetails &&
      userDetails.customerId &&
      isSuccess &&
      calledReducerType === "tasks/deleteTask" &&
      userDetails.carrier &&
      userDetails.system
    ) {
      dispatch(
        getTasks({
          customerId: userDetails.customerId,
          carrier: userDetails.carrier,
          system: userDetails.system,
        })
      );
    }
  }, [calledReducerType, dispatch, isSuccess, userDetails]);

  const handleTaskOkClick = () => {
    if (selectedTask && selectedTask.taskId) {
      dispatch(
        deleteTask({
          taskId: selectedTask.taskId,
          deleteComments: t("Deleting the task"),
          system: selectedTask.system,
          carrier: selectedTask.carrier,
        })
      );
    }
  };

  useEffect(() => {
    // Clear tasks when userDetails changes (user switching)
    if (userDetails) {
      dispatch(clearTasks());
    }
  }, [userDetails?.customerId, userDetails?.carrier, userDetails?.system, dispatch]);

  useEffect(() => {
    if (
      userDetails &&
      userDetails.customerId &&
      userDetails.carrier &&
      userDetails.system
    ) {
      dispatch(
        getTasks({
          customerId: userDetails.customerId,
          carrier: userDetails.carrier,
          system: userDetails.system,
        })
      );
    }
  }, [userDetails, dispatch]);

  const columns = [
    {
      label: t("Transaction Date"),
      accessorKey: "addDt",
      styles: TasksStyles.columnStyles.transactionDate,
      Cell: (task: any) => (
        <Typography sx={TasksStyles.transactionDateCell}>
          {`${moment(task.addDt).format(dateFormat)}`}
        </Typography>
      ),
    },
    {
      label: t("sr_category"),
      accessorKey: "name",
      styles: TasksStyles.columnStyles.category,
    },
    {
      label: t("Request Name"),
      accessorKey: "description",
      styles: TasksStyles.columnStyles.requestName,
    },
    {
      label: t("sr_description"),
      accessorKey: "note",
      styles: TasksStyles.columnStyles.description,
      Cell: (task: any) => (
        <Box sx={TasksStyles.descriptionCell}>
          <ReactMarkdown className="markdown">{task.note}</ReactMarkdown>
        </Box>
      ),
    },
    {
      label: "Status",
      accessorKey: "statusCd",
      styles: TasksStyles.columnStyles.status,
      Cell: (task: any) => (
        <Typography sx={TasksStyles.statusCell}>{task.statusCd}</Typography>
      ),
    },
    {
      label: "",
      accessorKey: "actions",
      styles: TasksStyles.columnStyles.actions,
      Cell: (task: any) => (
        <IconButton onClick={(event) => handleMoreClick(event, task)}>
          <MoreVertOutlined sx={TasksStyles.moreVertIcon} />
        </IconButton>
      ),
    },
  ];

  // Extract unique categories and statuses from tasks
  const categories = [
    "All Categories",
    ...Array.from(new Set(tasks.map((t) => t.name))).filter(Boolean),
  ];
  const statuses = [
    "All Statuses",
    ...Array.from(new Set(tasks.map((t) => t.statusCd))).filter(Boolean),
  ];

  // Filter tasks based on selected filters
  const filteredTasks = tasks.filter(
    (task) =>
      (selectedCategory === "All Categories" || task.name === selectedCategory) &&
      (selectedStatus === "All Statuses" || task.statusCd === selectedStatus)
  );

  const navigate = useNavigate();
  const handleNavigate = useCallback(() => {
    navigate("/customer/dashboard");
  }, [navigate]);
  return (
    <>
      {/* Filters and request count */}
      <Box sx={TasksStyles.headerContainer}>
        <Box sx={TasksStyles.backButtonContainer}>
          <ArrowBack
            sx={TasksStyles.backButton}
            onClick={handleNavigate}
          />
          <Typography variant="h5" sx={TasksStyles.heading}>
            {t("Service Requests")}
          </Typography>
        </Box>
        <Typography sx={TasksStyles.subtitle}>
          Manage and track your policy service requests
        </Typography>
        <Box sx={TasksStyles.filterContainer}>
          <Box sx={TasksStyles.filterLabel}>
            <FilterListIcon />
            <Typography sx={TasksStyles.filterLabelText}>Filters</Typography>
          </Box>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            size="small"
            sx={TasksStyles.select}
          >
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            size="small"
            sx={TasksStyles.select}
          >
            {statuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
          <Box sx={TasksStyles.flexGrow} />
          <Typography sx={TasksStyles.requestCount}>
            {filteredTasks.length} requests
          </Typography>
        </Box>
      </Box>
      {/* Table and dialogs */}
      <Box sx={TasksStyles.tasks}>
        <CustomTable
          columns={columns.filter((column) => {
            if (column.accessorKey === "actions") {
              return isFeatureAvailable({
                featureCodes: ["delete_task"],
                features,
              });
            }
            return true;
          })}
          data={[...filteredTasks].sort(
            (a, b) => new Date(b.addDt).getTime() - new Date(a.addDt).getTime()
          )}
          stickyHeader={true}
          pagination={true}
          styles={TasksStyles.tableContainer}
        />
      </Box>
      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        <MenuItem onClick={handleDeleteTaskClick}>
          <Box sx={TasksStyles.menuItem}>
            <DeleteOutline sx={TasksStyles.deleteIcon} />
            <Typography>{t("Delete")}</Typography>
          </Box>
        </MenuItem>
      </Menu>
      <Dialog open={deleteTaskDialogOpen} onClose={handleDeleteTaskDialogClose}>
        {taskMessage ||
        (isError && calledReducerType === "policyChange/PostRemoveVehicle") ? (
          <>
            <DialogTitle sx={TasksStyles.dialogTitle}>
              <CloseOutlined
                onClick={handleDeleteTaskDialogClose}
                sx={TasksStyles.closeIcon}
              />
            </DialogTitle>
            <DialogContent sx={TasksStyles.dialogContent}>
              {taskMessage ? (
                <img src={successIcon} alt="success-icon" />
              ) : (
                <ErrorIcon sx={{ color: "red" }} />
              )}
              {taskMessage ? (
                <DialogContentText>{taskMessage}</DialogContentText>
              ) : (
                <DialogContentText sx={TasksStyles.errorText}>
                  {t("Something went wrong please try again...")}
                </DialogContentText>
              )}
            </DialogContent>
          </>
        ) : (
          <>
            <DialogTitle>
              {t("Are you sure to delete this Service Request?")}
            </DialogTitle>
            <DialogActions>
              <Button variant="contained" onClick={handleDeleteTaskDialogClose}>
                {t("Cancel")}
              </Button>
              <Button variant="outlined" onClick={handleTaskOkClick}>
                {t("Ok")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default Tasks;
